if (!customElements.get('complementary-products-list')) {
  customElements.define(
    'complementary-products-list',
    class ComplementaryProductsList extends HTMLElement {
      connectedCallback() {
        this.toggleButton = this.querySelector('.complementary-product-list__toggle');
        this.extraProducts = this.querySelectorAll('.complementary-product-list__item--extra');
        this.carouselMediaQuery = window.matchMedia('(max-width: 989px)');
        this.productList = this.querySelector('.complementary-product-list');
        this.scrollIndicator = this.querySelector('.complementary-product-list__scroll-indicator');
        this.scrollThumb = this.querySelector('.complementary-product-list__scroll-thumb');
        this.initialiseScrollIndicator();

        if (!this.extraProducts.length) return;

        this.updateProductVisibility();
        if (this.dataset.responsiveReady !== 'true') {
          this.dataset.responsiveReady = 'true';
          this.carouselMediaQuery.addEventListener('change', () => this.updateProductVisibility());
        }

        if (!this.toggleButton || this.toggleButton.dataset.toggleReady === 'true') return;

        this.toggleButton.dataset.toggleReady = 'true';
        this.toggleButton.addEventListener('click', () => this.toggleProducts());
      }

      disconnectedCallback() {
        this.scrollIndicatorController?.abort();
        this.scrollIndicatorObserver?.disconnect();
        this.scrollIndicator?.removeAttribute('data-scroll-ready');
        this.productList?.classList.remove('is-scrollbar-dragging');
        this.scrollIndicator?.classList.remove('is-dragging');

        if (this.scrollIndicatorFrame) cancelAnimationFrame(this.scrollIndicatorFrame);
      }

      initialiseScrollIndicator() {
        if (!this.productList || !this.scrollIndicator || !this.scrollThumb) return;
        if (this.scrollIndicator.dataset.scrollReady === 'true') return;

        this.scrollIndicator.dataset.scrollReady = 'true';
        this.scrollIndicatorController = new AbortController();
        const { signal } = this.scrollIndicatorController;

        const scheduleUpdate = () => {
          if (this.scrollIndicatorFrame) return;

          this.scrollIndicatorFrame = requestAnimationFrame(() => {
            this.scrollIndicatorFrame = null;
            this.updateScrollIndicator();
          });
        };
        const onPointerDown = (event) => {
          const indicatorRect = this.scrollIndicator.getBoundingClientRect();
          const thumbRect = this.scrollThumb.getBoundingClientRect();
          const maximumScroll = Math.max(0, this.productList.scrollWidth - this.productList.clientWidth);
          const maximumTravel = Math.max(0, indicatorRect.width - thumbRect.width);

          if (maximumScroll <= 0 || maximumTravel <= 0) return;

          event.preventDefault();

          if (event.clientX >= thumbRect.left && event.clientX <= thumbRect.right) {
            this.scrollDragging = true;
            this.scrollDragStartX = event.clientX;
            this.scrollDragStartLeft = this.productList.scrollLeft;
            this.scrollDragMaximumScroll = maximumScroll;
            this.scrollDragMaximumTravel = maximumTravel;
            this.productList.classList.add('is-scrollbar-dragging');
            this.scrollIndicator.classList.add('is-dragging');
            this.scrollIndicator.setPointerCapture?.(event.pointerId);
            return;
          }

          const clickPosition = event.clientX - indicatorRect.left - thumbRect.width / 2;
          const progress = Math.min(1, Math.max(0, clickPosition / maximumTravel));
          this.productList.scrollTo({
            left: progress * maximumScroll,
            behavior: 'smooth',
          });
        };
        const onPointerMove = (event) => {
          if (!this.scrollDragging) return;

          event.preventDefault();
          const delta = event.clientX - this.scrollDragStartX;
          const scrollLeft =
            this.scrollDragStartLeft +
            delta * (this.scrollDragMaximumScroll / this.scrollDragMaximumTravel);
          this.productList.scrollLeft = Math.min(
            this.scrollDragMaximumScroll,
            Math.max(0, scrollLeft)
          );
        };
        const endPointerDrag = (event) => {
          if (!this.scrollDragging) return;

          this.scrollDragging = false;
          this.productList.classList.remove('is-scrollbar-dragging');
          this.scrollIndicator.classList.remove('is-dragging');
          if (this.scrollIndicator.hasPointerCapture?.(event.pointerId)) {
            this.scrollIndicator.releasePointerCapture(event.pointerId);
          }
        };
        const onKeyDown = (event) => {
          const maximumScroll = Math.max(0, this.productList.scrollWidth - this.productList.clientWidth);
          if (maximumScroll <= 0) return;

          let nextScrollLeft = null;
          if (event.key === 'ArrowLeft') {
            nextScrollLeft = this.productList.scrollLeft - this.productList.clientWidth * 0.8;
          }
          if (event.key === 'ArrowRight') {
            nextScrollLeft = this.productList.scrollLeft + this.productList.clientWidth * 0.8;
          }
          if (event.key === 'Home') nextScrollLeft = 0;
          if (event.key === 'End') nextScrollLeft = maximumScroll;

          if (nextScrollLeft === null) return;

          event.preventDefault();
          this.productList.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
        };

        this.scrollDragging = false;
        this.scrollDragStartX = 0;
        this.scrollDragStartLeft = 0;
        this.scrollDragMaximumScroll = 0;
        this.scrollDragMaximumTravel = 0;

        this.productList.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
        window.addEventListener('resize', scheduleUpdate, { passive: true, signal });
        this.scrollIndicator.addEventListener('pointerdown', onPointerDown, { signal });
        window.addEventListener('pointermove', onPointerMove, { passive: false, signal });
        window.addEventListener('pointerup', endPointerDrag, { signal });
        window.addEventListener('pointercancel', endPointerDrag, { signal });
        this.scrollIndicator.addEventListener('keydown', onKeyDown, { signal });

        if ('ResizeObserver' in window) {
          this.scrollIndicatorObserver = new ResizeObserver(scheduleUpdate);
          this.scrollIndicatorObserver.observe(this.productList);
          this.scrollIndicatorObserver.observe(this.scrollIndicator);
        }

        scheduleUpdate();
      }

      updateScrollIndicator() {
        const scrollWidth = this.productList.scrollWidth;
        const visibleWidth = this.productList.clientWidth;
        const trackWidth = this.scrollIndicator.clientWidth;
        const maximumScroll = Math.max(0, scrollWidth - visibleWidth);
        const visibleRatio = scrollWidth > 0 ? Math.min(1, visibleWidth / scrollWidth) : 1;
        const thumbWidth = Math.min(trackWidth, Math.max(24, trackWidth * visibleRatio));
        const maximumTravel = Math.max(0, trackWidth - thumbWidth);
        const progress =
          maximumScroll > 0 ? Math.min(1, Math.max(0, this.productList.scrollLeft / maximumScroll)) : 0;

        this.scrollThumb.style.width = `${thumbWidth}px`;
        this.scrollThumb.style.transform = `translateX(${maximumTravel * progress}px)`;
        this.scrollIndicator.classList.toggle('is-hidden', maximumScroll <= 1);
        this.scrollIndicator.hidden = maximumScroll <= 1;
        this.scrollIndicator.setAttribute('aria-hidden', String(maximumScroll <= 1));
        this.scrollIndicator.setAttribute('tabindex', maximumScroll > 1 ? '0' : '-1');
        this.scrollIndicator.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
        this.scrollIndicator.setAttribute('aria-valuetext', `${Math.round(progress * 100)}% scrolled`);
      }

      updateProductVisibility() {
        const usesCarousel = this.carouselMediaQuery.matches;
        const isExpanded = this.toggleButton?.getAttribute('aria-expanded') === 'true';

        this.extraProducts.forEach((product) => {
          product.hidden = !usesCarousel && !isExpanded;
        });
      }

      toggleProducts() {
        const isExpanded = this.toggleButton.getAttribute('aria-expanded') === 'true';
        const nextExpandedState = !isExpanded;

        this.toggleButton.setAttribute('aria-expanded', String(nextExpandedState));
        this.updateProductVisibility();
        this.toggleButton.querySelector('span').textContent = nextExpandedState
          ? this.toggleButton.dataset.viewLessLabel
          : this.toggleButton.dataset.viewMoreLabel;

        if (isExpanded) {
          requestAnimationFrame(() => this.scrollToSectionHeading());
        }
      }

      scrollToSectionHeading() {
        const sectionHeading = this.closest('.product__accordion')?.querySelector('summary');
        if (!sectionHeading) return;

        const header = document.querySelector('.gs-header');
        const headerContainer = header?.closest('.shopify-section') || header;
        const headerPosition = headerContainer ? window.getComputedStyle(headerContainer).position : '';
        const headerOffset =
          header && ['fixed', 'sticky'].includes(headerPosition) ? header.getBoundingClientRect().height : 0;
        const scrollTop = sectionHeading.getBoundingClientRect().top + window.scrollY - headerOffset - 16;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        window.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
      }
    }
  );
}
